import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePropertyField,
  usePropertyAddressUrl,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import {
  MultihubLogoMonoWhite,
  repliWhiteLogoUrl,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons';

// ============================================================================
// Helpers
// ============================================================================

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function toTelUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('tel:')) return raw;
  const digits = raw.replace(/[^+\d]/g, '');
  return digits ? `tel:${digits}` : raw;
}

function toSmsUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('sms:')) return raw;
  const digits = raw.replace(/[^+\d]/g, '');
  return digits ? `sms:${digits}` : raw;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined' && normalized !== '0' && normalized !== 'closed';
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface GroupedHours {
  label: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const sorted = [...raw].sort((a, b) => {
    const ai = DAY_ORDER.indexOf(normalizeDay(a.day || ''));
    const bi = DAY_ORDER.indexOf(normalizeDay(b.day || ''));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const groups: GroupedHours[] = [];

  for (const entry of sorted) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    const dayLabel = DAY_LABELS[key] || entry.day;
    const isClosed = !hasValue(entry.open_time) || !hasValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
    } else {
      groups.push({ label: dayLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// SVG Icons — matching the Header's stroke-style outline icons
// ============================================================================

function MapPinIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function PhoneIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function SmartphoneIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  );
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const bgImageSlotDef: SlotGroupDefinition = {
  label: 'Footer Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule a Visit Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Visit' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentLoginLinkDef: SlotGroupDefinition = {
  label: 'Resident Login Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Login' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_apply_now_url' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const contactUsLinkDef: SlotGroupDefinition = {
  label: 'Contact Us Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: { label: 'Address Text', type: 'text', defaultField: 'property_full_address', defaultValue: '123 Main Street, City, ST 00000' },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone Link',
  fields: {
    text: { label: 'Phone Text', type: 'text', defaultField: 'property_call_tracking_phone' },
    url: { label: 'URL', type: 'url', defaultField: 'property_call_tracking_phone' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const textUsLinkDef: SlotGroupDefinition = {
  label: 'Text Us Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Text Us!' },
    url: { label: 'URL', type: 'url', defaultField: 'property_call_tracking_phone' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_white_logo', suggestedFields: ['property_company_mono_white_logo', 'property_company_logo', 'property_company_mono_black_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility Page' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url' },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url' },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const propertyName = useContentToken('footer.property_name');
  const callTrackingPhone = usePropertyField('property_call_tracking_phone') as string | null | undefined;
  const propertyPhone = usePropertyField('property_phone') as string | null | undefined;
  const propertyAddressUrl = usePropertyAddressUrl();

  // Slot groups
  const bgImage = useSlotGroup('footer.bg_image', bgImageSlotDef);
  const logo = useSlotGroup('footer.logo', logoSlotDef);
  const scheduleTourLink = useSlotGroup('footer.schedule_tour_link', scheduleTourLinkDef);
  const residentLoginLink = useSlotGroup('footer.resident_login_link', residentLoginLinkDef);
  const applyNowLink = useSlotGroup('footer.apply_now_link', applyNowLinkDef);
  const contactUsLink = useSlotGroup('footer.contact_us_link', contactUsLinkDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  const phoneLink = useSlotGroup('footer.phone_link', phoneLinkDef);
  const textUsLink = useSlotGroup('footer.text_us_link', textUsLinkDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);
  const facebookLink = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const instagramLink = useSlotGroup('footer.instagram_link', instagramLinkDef);

  // Office hours
  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });
  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  // Derived URLs
  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const phoneHref = toTelUrl(phoneLink.url) || toTelUrl(callTrackingPhone) || toTelUrl(propertyPhone);
  const textUsHref = toSmsUrl(textUsLink.url) || toSmsUrl(callTrackingPhone) || toSmsUrl(propertyPhone);
  const phoneDisplay = formatPhone(phoneLink.text) || formatPhone(callTrackingPhone) || formatPhone(propertyPhone) || '(555) 555-5555';
  const bgSrc = bgImage.image || '';

  return (
    <footer>

      {/* ================================================================ */}
      {/* SECTION 1: Background Image with CTA Bar + Contact Info         */}
      {/* ================================================================ */}
      {/* `isolate` keeps the inner z-10 bars (CTA bar, banner, contact row) in
          their own stacking context so they don't out-stack fixed-position
          third-party widgets (e.g. chatbots) at the page root. */}
      <div className="footer-hero-section relative isolate text-white overflow-hidden">
        {/* Background image */}
        <EditableSlotGroup groupPath="footer.bg_image" definition={bgImageSlotDef}>
          <div className="absolute inset-0">
            {bgSrc ? (
              <VibesitesImage
                url={bgSrc}
                alt={bgImage.alt || ''}
                objectFit="cover"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gray-800" />
            )}
            {/* Dark overlay */}
            <div className="absolute inset-0 bg-black/60" />
          </div>
        </EditableSlotGroup>

        {/* CTA Links Bar — full width */}
        <div className="relative z-10 bg-black/80">
          <div className="grid grid-cols-2 md:grid-cols-4">
            <EditableSlotGroup groupPath="footer.schedule_tour_link" definition={scheduleTourLinkDef}>
              <a
                href={scheduleTourLink.url || '/schedule-a-tour'}
                target={scheduleTourLink._target || '_self'}
                className="footer-cta-link"
              >
                {scheduleTourLink.label || 'Schedule a Visit'}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="footer.resident_login_link" definition={residentLoginLinkDef}>
              <a
                href={residentLoginLink.url || '/resident-access'}
                target={residentLoginLink._target || '_self'}
                className="footer-cta-link"
              >
                {residentLoginLink.label || 'Resident Login'}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="footer.apply_now_link" definition={applyNowLinkDef}>
              <a
                href={applyNowLink.url || '/apply-now'}
                target={applyNowLink._target || '_self'}
                className="footer-cta-link"
              >
                {applyNowLink.label || 'Apply Now'}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="footer.contact_us_link" definition={contactUsLinkDef}>
              <a
                href={contactUsLink.url || '/contact'}
                target={contactUsLink._target || '_self'}
                className="footer-cta-link"
              >
                {contactUsLink.label || 'Contact Us'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>

        {/* "Let's Talk" Banner */}
        <div className="relative z-10 px-4 sm:px-6 lg:px-8 pt-10 md:pt-14">
          <div className="max-w-5xl mx-auto">
            <div className="border border-white/60 py-4 md:py-5 px-6 text-center">
              <RichTextContent
                path="footer.lets_talk_heading"
                defaultValue="<p>LET'S TALK ABOUT YOUR NEW HOME</p>"
                as="p"
                className="font-heading text-base sm:text-lg md:text-xl lg:text-2xl tracking-[0.15em] uppercase text-white"
              />
            </div>
          </div>
        </div>

        {/* Contact Info Row */}
        <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 items-start">

              {/* Address */}
              <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
                <a
                  href={addressHref}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 group"
                >
                  <span className="footer-icon-badge flex-shrink-0">
                    <MapPinIcon className="w-5 h-5" />
                  </span>
                  <span className="font-heading text-lg md:text-xl text-white group-hover:text-white/80 transition-colors">
                    {addressLink.text || '123 Main Street, City, ST 00000'}
                  </span>
                </a>
              </EditableSlotGroup>

              {/* Phone + Text Us */}
              <div className="flex flex-col gap-5">
                <EditableSlotGroup groupPath="footer.phone_link" definition={phoneLinkDef}>
                  <a
                    href={phoneHref || '#'}
                    className="flex items-center gap-3 group dniphone dniphonehref"
                  >
                    <span className="footer-icon-badge flex-shrink-0">
                      <PhoneIcon className="w-5 h-5" />
                    </span>
                    <span className="font-heading text-lg md:text-xl text-white group-hover:text-white/80 transition-colors">
                      {phoneDisplay}
                    </span>
                  </a>
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="footer.text_us_link" definition={textUsLinkDef}>
                  <a
                    href={textUsHref || '#'}
                    className="flex items-center gap-3 group"
                  >
                    <span className="footer-icon-badge flex-shrink-0">
                      <SmartphoneIcon className="w-5 h-5" />
                    </span>
                    <span className="font-heading text-lg md:text-xl text-white group-hover:text-white/80 transition-colors">
                      {textUsLink.text || 'Text Us!'}
                    </span>
                  </a>
                </EditableSlotGroup>
              </div>

              {/* Office Hours */}
              <div className="text-center md:text-right">
                <RichTextContent
                  path="footer.office_hours_label"
                  defaultValue="<p>Office Hours</p>"
                  as="h3"
                  className="font-heading text-lg md:text-xl text-white font-bold mb-3"
                />
                {groupedHours.length > 0 ? (
                  <dl className="space-y-1">
                    {groupedHours.map((group, index) => (
                      <div key={index} className="flex justify-center md:justify-end gap-6 font-body text-sm md:text-base text-white/90">
                        <dt className="min-w-[70px] text-right">{group.label}</dt>
                        <dd className="min-w-[120px] text-left md:text-right">{group.hours}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="font-body text-sm text-white/50">Office hours will be displayed here.</p>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* ================================================================ */}
      {/* SECTION 2: Dark Footer — Logo, Nav, Social                      */}
      {/* ================================================================ */}
      <div className="bg-[#1a1a1a] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-14">
          <div className="flex flex-col items-center gap-8">

            {/* Logo */}
            <EditableSlotGroup groupPath="footer.logo" definition={logoSlotDef}>
              <a href="/" className="block">
                {logo.image ? (
                  <VibesitesImage
                    url={logo.image}
                    alt={logo.alt || 'Community Logo'}
                    className="h-16 w-auto max-w-[220px] object-contain"
                  />
                ) : (
                  <div className="font-heading text-2xl text-white">Our Community</div>
                )}
              </a>
            </EditableSlotGroup>

            {/* Navigation Links */}
            <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-body text-sm text-gray-300" aria-label="Footer links">
              <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
                <a
                  href={sitemapLink.url || '/sitemap'}
                  target={sitemapLink._target || '_self'}
                  className="hover:text-white transition-colors"
                >
                  {sitemapLink.text || 'Sitemap'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
                <a
                  href={accessibilityLink.url || '/accessibility'}
                  target={accessibilityLink._target || '_self'}
                  className="hover:text-white transition-colors"
                >
                  {accessibilityLink.text || 'Accessibility Page'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
                <a
                  href={privacyPolicyLink.url || '/privacy-policy'}
                  target={privacyPolicyLink._target || '_self'}
                  className="hover:text-white transition-colors"
                >
                  {privacyPolicyLink.text || 'Privacy Policy'}
                </a>
              </EditableSlotGroup>
            </nav>

            {/* Social Media */}
            <div className="flex items-center gap-4">
              <RichTextContent
                path="footer.social_label"
                defaultValue="<p>Social Media</p>"
                as="span"
                className="font-body text-sm text-gray-300"
              />
              <div className="flex items-center gap-3">
                <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
                  {facebookLink.url ? (
                    <a
                      href={facebookLink.url}
                      target={facebookLink._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label={facebookLink.label || 'Facebook'}
                      className="text-gray-300 hover:text-white transition-colors"
                    >
                      <FontAwesomeIcon icon={faFacebook} className="text-xl" />
                    </a>
                  ) : (
                    <span className="text-gray-500">
                      <FontAwesomeIcon icon={faFacebook} className="text-xl" />
                    </span>
                  )}
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
                  {instagramLink.url ? (
                    <a
                      href={instagramLink.url}
                      target={instagramLink._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label={instagramLink.label || 'Instagram'}
                      className="text-gray-300 hover:text-white transition-colors"
                    >
                      <FontAwesomeIcon icon={faInstagram} className="text-xl" />
                    </a>
                  ) : (
                    <span className="text-gray-500">
                      <FontAwesomeIcon icon={faInstagram} className="text-xl" />
                    </span>
                  )}
                </EditableSlotGroup>
              </div>
            </div>

          </div>
        </div>

        {/* ---- Divider ---- */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <hr className="border-t border-white/10" />
        </div>

        {/* ---- Bottom Attribution Bar ---- */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">

            {/* Managed By — left */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <EditableSlotGroup groupPath="footer.managed_by_logo" definition={managedByLogoDef}>
                {managedByLogo.image ? (
                  <a href={managedByLogo.url || '#'} target={managedByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-90 hover:opacity-100 transition-opacity">
                    <VibesitesImage
                      url={managedByLogo.image}
                      alt={managedByLogo.alt || 'Property Management Company'}
                      className="h-8 w-auto max-w-[160px] object-contain"
                    />
                  </a>
                ) : null}
              </EditableSlotGroup>
            </div>

            {/* Copyright — center */}
            <p className="font-body text-[11px] text-gray-400 text-center">
              &copy; {new Date().getFullYear()}{' '}
              <EditableToken
                path="footer.property_name"
                defaultField="property_name"
                defaultValue="Property Name"
              >
                {propertyName || 'Property Name'}
              </EditableToken>
              {' '}All Rights Reserved. View{' '}
              <a
                href={privacyPolicyLink.url || '/privacy-policy'}
                className="underline hover:text-gray-200 transition-colors"
              >
                Privacy Policy
              </a>.
            </p>

            {/* Multihub + Repli logos — right, side by side */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <EditableSlotGroup groupPath="footer.powered_by_logo" definition={poweredByLogoDef}>
                <a href={poweredByLogo.url || 'https://www.multihub.io/'} target={poweredByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-90 hover:opacity-100 transition-opacity">
                  <MultihubLogoMonoWhite height={20} alt={poweredByLogo.alt || 'Multihub'} />
                </a>
              </EditableSlotGroup>
              <span className="text-gray-400" aria-hidden="true">·</span>
              <EditableSlotGroup groupPath="footer.design_by_logo" definition={designByLogoDef}>
                <a href={designByLogo.url || 'https://www.repli360.com/'} target={designByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-90 hover:opacity-100 transition-opacity">
                  <VibesitesImage
                    url={repliWhiteLogoUrl}
                    alt={designByLogo.alt || 'Repli'}
                    height={28}
                    className="h-7 w-auto"
                    priority
                  />
                </a>
              </EditableSlotGroup>
            </div>

          </div>
        </div>
      </div>

      {/* ================================================================ */}
      {/* Scoped Styles                                                   */}
      {/* ================================================================ */}
      <style>{`
        .footer-cta-link {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.875rem 1rem;
          font-family: var(--vibe-font-heading, var(--vibe-font-body, sans-serif));
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #fff;
          text-decoration: none;
          transition: background-color 0.25s ease, color 0.25s ease;
        }
        .footer-cta-link:hover {
          background-color: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
        }
        @media (min-width: 768px) {
          .footer-cta-link {
            font-size: 0.8rem;
            padding: 1rem 1.25rem;
          }
        }
        .footer-icon-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
          flex-shrink: 0;
          color: #fff;
        }
      `}</style>

    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.property_name': {
      label: 'Property Name',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Property Name',
    },
    'footer.lets_talk_heading': {
      label: 'Let\'s Talk Heading',
      type: 'richtext',
      defaultValue: '<p>LET\'S TALK ABOUT YOUR NEW HOME</p>',
    },
    'footer.office_hours_label': {
      label: 'Office Hours Label',
      type: 'richtext',
      defaultValue: '<p>Office Hours</p>',
    },
    'footer.social_label': {
      label: 'Social Media Label',
      type: 'richtext',
      defaultValue: '<p>Social Media</p>',
    },
  },
  slotGroups: {
    'footer.bg_image': bgImageSlotDef,
    'footer.logo': logoSlotDef,
    'footer.schedule_tour_link': scheduleTourLinkDef,
    'footer.resident_login_link': residentLoginLinkDef,
    'footer.apply_now_link': applyNowLinkDef,
    'footer.contact_us_link': contactUsLinkDef,
    'footer.address': addressLinkDef,
    'footer.phone_link': phoneLinkDef,
    'footer.text_us_link': textUsLinkDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.instagram_link': instagramLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
